import React, { useState } from 'react'
import { Helmet } from 'react-helmet-async'
import Preloader from '../components/Preloader'
import Header from '../components/Header'
import ScrollReveal from '../components/ScrollReveal'
import Footer from '../components/Footer'
import ExternalVRTour from '../components/ExternalVRTour'
import { FiArrowRight } from 'react-icons/fi'
import { FaChartLine, FaGlobeAfrica, FaMobileAlt, FaPlayCircle, FaRocket } from 'react-icons/fa'
import { MdOutlineDesignServices } from 'react-icons/md'
import { getTourUrl } from '../config/tourConfig'

const tours = [
    {
        id: 1,
        title: 'East Legon 360 Aerial View Tour',
        category: 'Aerial / Lifestyle',
        mediaType: 'video',
        mediaSrc: '/assets/eastlegon360arielview.mp4',
        description: 'A cinematic aerial tour that showcases the rhythm, design, and surrounding landscape of East Legon in Accra.',
        link: 'https://vrifyafrica.com/virtual-tour'
    },
    {
        id: 2,
        title: 'Bel Air Crest Hotel Virtual Tour',
        category: 'Hospitality',
        mediaType: 'image',
        mediaSrc: '/assets/belaircrest.avif',
        description: 'A polished hospitality experience that brings the design, comfort, and atmosphere of Bel Air Crest to life online.',
        link: 'https://vrifyafrica.com/virtual-tour'
    },
    {
        id: 3,
        title: 'BridgeView Resort and LakeSide Virtual Tour',
        category: 'Hospitality / Resort',
        mediaType: 'image',
        mediaSrc: '/assets/bridgeviewresortandlakeside.avif',
        description: 'A calm, premium resort walkthrough that highlights the property’s landscapes, amenities, and guest experience.',
        link: 'https://vrifyafrica.com/virtual-tour'
    }
]

const services = [
    {
        title: 'Immersive tours for real estate',
        description: 'Sell properties faster with story-driven tours that highlight key spaces, layouts, and lifestyle details.',
        icon: <FaGlobeAfrica className="text-2xl text-yellow-300" />
    },
    {
        title: 'Responsive portfolio experiences',
        description: 'Every tour is tuned to work beautifully across phone, tablet, and desktop screens.',
        icon: <FaMobileAlt className="text-2xl text-yellow-300" />
    },
    {
        title: 'Performance-focused delivery',
        description: 'We optimize media, navigation, and loading speed so your audience stays engaged from the first click.',
        icon: <FaChartLine className="text-2xl text-yellow-300" />
    },
    {
        title: 'Launch-ready showcase pages',
        description: 'Each tour is packaged with a premium presentation layer that feels modern and client-ready.',
        icon: <FaRocket className="text-2xl text-yellow-300" />
    }
]

const processSteps = [
    {
        title: 'Discover',
        description: 'We align on the story, audience, and key spaces you want your viewers to explore.'
    },
    {
        title: 'Design',
        description: 'We shape the visual direction, navigation flow, and brand-consistent presentation layer.'
    },
    {
        title: 'Develop',
        description: 'We build the embedded experience, polish media quality, and ensure fast loading on every device.'
    },
    {
        title: 'Launch',
        description: 'We publish the tour, make it easy to share, and help you convert attention into engagement.'
    }
]

const stats = [
    { value: '5+', label: 'portfolio tours' },
    { value: '100%', label: 'responsive viewing' },
    { value: '24/7', label: 'tour availability' },
    { value: 'Fast', label: 'launch support' }
]

const VirtualTours = () => {
    const myTourUrl = getTourUrl('DEFAULT')
    const [currentPage, setCurrentPage] = useState(1)
    const toursPerPage = 6

    const indexOfLastTour = currentPage * toursPerPage
    const indexOfFirstTour = indexOfLastTour - toursPerPage
    const currentTours = tours.slice(indexOfFirstTour, indexOfLastTour)
    const totalPages = Math.ceil(tours.length / toursPerPage)

    return (
        <div>
            <Helmet>
                <title>Virtual Tours | VRIFY Technologies</title>
                <meta
                    name="description"
                    content="Modern virtual tour portfolio for real estate, hospitality, and immersive digital experiences by VRIFY Technologies."
                />
            </Helmet>

            <div className="font-nanum-myeongjo min-h-screen bg-slate-950 text-slate-100 relative overflow-hidden">
                <div className="fixed inset-0 -z-10">
                    <ExternalVRTour tourUrl={myTourUrl} />
                </div>
                <div className="fixed inset-0 -z-10 bg-black/45 backdrop-blur-sm" />
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(250,204,21,0.20),_transparent_24%),radial-gradient(circle_at_bottom_right,_rgba(59,130,246,0.14),_transparent_20%)]" />

                <div className="relative z-10">
                    <Header />
                    <Preloader />

                    <section className="mx-auto max-w-7xl px-4 pb-10 pt-16 sm:px-6 lg:px-8 lg:pb-16">
                        <ScrollReveal>
                            <div className="grid items-center gap-10 lg:grid-cols-[1.1fr_0.9fr] lg:gap-12">
                                <div>
                                    <p className="inline-flex items-center gap-2 rounded-full border border-yellow-400/50 bg-yellow-400/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.3em] text-yellow-300">
                                        <MdOutlineDesignServices className="text-sm" />
                                        Virtual tours
                                    </p>
                                    <h1 className="mt-5 text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
                                        A modern virtual tour portfolio that makes every space feel unforgettable.
                                    </h1>
                                    <p className="mt-4 max-w-2xl text-sm leading-7 text-slate-200 sm:text-base">
                                        Showcase real estate, hospitality, and immersive spaces with a premium online experience that is responsive, fast, and easy to explore.
                                    </p>

                                    <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                                        <a
                                            href="#portfolio"
                                            className="inline-flex items-center justify-center rounded-full bg-yellow-400 px-5 py-3 text-sm font-bold text-slate-950 transition hover:bg-yellow-300"
                                        >
                                            Explore the portfolio
                                        </a>
                                        <a
                                            href="/contactus"
                                            className="inline-flex items-center justify-center rounded-full border border-slate-700 px-5 py-3 text-sm font-semibold text-white transition hover:border-yellow-300 hover:text-yellow-200"
                                        >
                                            Request a custom tour
                                        </a>
                                    </div>

                                    <div className="mt-8 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                                        {stats.map((stat) => (
                                            <div
                                                key={stat.label}
                                                className="rounded-2xl border border-white/10 bg-white/5 px-4 py-4 backdrop-blur-sm"
                                            >
                                                <p className="text-xl font-bold text-white">{stat.value}</p>
                                                <p className="mt-1 text-[11px] uppercase tracking-[0.2em] text-slate-300">{stat.label}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="rounded-[28px] border mt-16 border-white/10 bg-slate-900/85 p-5 shadow-2xl shadow-black/30 backdrop-blur sm:p-6">
                                    <div className="rounded-[24px] bg-gradient-to-br from-yellow-400 via-amber-300 to-slate-100 p-[1px]">
                                        <div className="rounded-[23px] bg-slate-950/95 p-5 sm:p-6">
                                            <p className="text-[11px] uppercase tracking-[0.3em] text-yellow-300">Why teams choose us</p>
                                            <div className="mt-5 space-y-4">
                                                {services.map((service, index) => (
                                                    <div key={service.title} className="rounded-2xl border border-white/10 bg-white/5 p-4">
                                                        <div className="flex items-start gap-3">
                                                            <div className="mt-0.5 rounded-xl bg-slate-900 p-2">
                                                                {service.icon}
                                                            </div>
                                                            <div>
                                                                <p className="text-sm font-semibold text-white">{service.title}</p>
                                                                <p className="mt-1 text-sm leading-6 text-slate-300">{service.description}</p>
                                                            </div>
                                                        </div>
                                                        {index < services.length - 1 ? <div className="mt-4 h-px bg-white/10" /> : null}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </ScrollReveal>
                    </section>

                    <section className="mx-auto max-w-7xl px-4 pb-10 sm:px-6 lg:px-8">
                        <ScrollReveal>
                            <div className="flex flex-col gap-3 text-center sm:text-left">
                                <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-yellow-300">How we work</p>
                                <h2 className="text-2xl font-bold text-white sm:text-3xl">A clear process that keeps every tour polished and launch-ready.</h2>
                                <p className="max-w-2xl text-sm leading-7 text-slate-300 sm:text-base">
                                    Each project is shaped for speed, presentation quality, and a viewer experience that feels premium on every screen.
                                </p>
                            </div>
                        </ScrollReveal>

                        <div className="mt-8 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                            {processSteps.map((step, index) => (
                                <ScrollReveal key={step.title}>
                                    <div className="rounded-[24px] border border-white/10 bg-white/5 p-5 shadow-lg shadow-black/10">
                                        <div className="flex items-center justify-between">
                                            <p className="text-[11px] uppercase tracking-[0.3em] text-yellow-300">0{index + 1}</p>
                                            <FaPlayCircle className="text-lg text-yellow-300" />
                                        </div>
                                        <h3 className="mt-4 text-lg font-semibold text-white">{step.title}</h3>
                                        <p className="mt-2 text-sm leading-7 text-slate-300">{step.description}</p>
                                    </div>
                                </ScrollReveal>
                            ))}
                        </div>
                    </section>

                    <section id="portfolio" className="mx-auto max-w-7xl px-4 pb-4 pt-8 sm:px-6 lg:px-8">
                        <ScrollReveal>
                            <div className="flex flex-col gap-3 text-center sm:text-left">
                                <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-yellow-300">Portfolio</p>
                                <h2 className="text-2xl font-bold text-white sm:text-3xl">Selected virtual tours that reflect the VRIFY style.</h2>
                                <p className="max-w-2xl text-sm leading-7 text-slate-300 sm:text-base">
                                    Browse our featured virtual tours designed to feel immersive, polished, and easy to share with clients, buyers, and guests.
                                </p>
                            </div>
                        </ScrollReveal>

                        <div className="mt-8 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
                            {currentTours.map((tour) => (
                                <ScrollReveal key={tour.id}>
                                    <article className="flex h-full flex-col overflow-hidden rounded-[24px] border border-white/10 bg-slate-900/85 shadow-xl shadow-black/20 transition duration-300 hover:-translate-y-1 hover:border-yellow-300/70">
                                        <div className="relative h-56 overflow-hidden">
                                            {tour.mediaType === 'video' ? (
                                                <video
                                                    src={tour.mediaSrc}
                                                    autoPlay
                                                    loop
                                                    muted
                                                    playsInline
                                                    className="h-full w-full object-cover transition duration-500 hover:scale-105"
                                                />
                                            ) : (
                                                <img
                                                    src={tour.mediaSrc}
                                                    alt={tour.title}
                                                    className="h-full w-full object-cover transition duration-500 hover:scale-105"
                                                    loading="lazy"
                                                />
                                            )}
                                            <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/20 to-transparent" />
                                            <span className="absolute left-4 top-4 rounded-full bg-yellow-400 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.2em] text-slate-950">
                                                {tour.category}
                                            </span>
                                        </div>

                                        <div className="flex flex-1 flex-col p-5">
                                            <h3 className="text-lg font-semibold text-white">{tour.title}</h3>
                                            <p className="mt-3 flex-1 text-sm leading-7 text-slate-300">{tour.description}</p>
                                            <a
                                                href={tour.link}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="mt-5 inline-flex items-center justify-center gap-2 rounded-full bg-yellow-400 px-4 py-2 text-sm font-bold text-slate-950 transition hover:bg-yellow-300"
                                            >
                                                View tour
                                                <FiArrowRight className="text-sm" />
                                            </a>
                                        </div>
                                    </article>
                                </ScrollReveal>
                            ))}
                        </div>

                        {totalPages > 1 && (
                            <div className="mt-8 flex flex-wrap justify-center gap-2 pb-4">
                                <button
                                    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                                    disabled={currentPage === 1}
                                    className="rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                                >
                                    Previous
                                </button>
                                {[...Array(totalPages)].map((_, idx) => (
                                    <button
                                        key={idx}
                                        onClick={() => setCurrentPage(idx + 1)}
                                        className={`rounded-full px-4 py-2 text-sm font-semibold ${currentPage === idx + 1 ? 'bg-yellow-400 text-slate-950' : 'border border-white/10 bg-white/5 text-white'}`}
                                    >
                                        {idx + 1}
                                    </button>
                                ))}
                                <button
                                    onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                                    disabled={currentPage === totalPages}
                                    className="rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                                >
                                    Next
                                </button>
                            </div>
                        )}
                    </section>

                    <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
                        <ScrollReveal>
                            <div className="rounded-[30px] border border-yellow-300/30 bg-gradient-to-r from-yellow-400 via-amber-300 to-yellow-200 px-5 py-8 sm:px-8 lg:flex lg:items-center lg:justify-between lg:px-10">
                                <div className="max-w-2xl">
                                    <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-slate-950">Ready to showcase</p>
                                    <h2 className="mt-3 text-2xl font-bold text-slate-950 sm:text-3xl">
                                        Bring your next property, resort, or experience to life with a premium tour portfolio.
                                    </h2>
                                    <p className="mt-3 text-sm leading-7 text-slate-900/80 sm:text-base">
                                        If you want a more immersive presentation for clients, guests, or investors, we can help you design and launch a tour that feels polished and trustworthy.
                                    </p>
                                </div>
                                <div className="mt-6 flex flex-wrap gap-3 lg:mt-0">
                                    <a
                                        href="/contactus"
                                        className="inline-flex items-center justify-center rounded-full bg-slate-950 px-5 py-3 text-sm font-bold text-yellow-300 transition hover:bg-slate-900"
                                    >
                                        Contact us
                                    </a>
                                    <a
                                        href="/virtual-reality"
                                        className="inline-flex items-center justify-center rounded-full border border-slate-950 px-5 py-3 text-sm font-bold text-slate-950 transition hover:bg-slate-950 hover:text-yellow-300"
                                    >
                                        Explore other services
                                    </a>
                                </div>
                            </div>
                        </ScrollReveal>
                    </section>

                    <Footer />
                </div>
            </div>
        </div>
    )
}

export default VirtualTours
